import React, { useState, useEffect } from 'react';
import Login from './Login';
import Register from './Register';
import ResetPassword from './ResetPassword';
import { apiService } from '../../services/apiService';
import { notificationService } from '../../services/notificationService';

interface AuthWrapperProps {
  children: React.ReactNode;
}

interface User {
  id: number;
  email: string;
  username: string;
  full_name?: string;
  avatar_url?: string;
  is_verified: boolean;
  is_admin?: boolean;
}

const AuthWrapper: React.FC<AuthWrapperProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [authMode, setAuthMode] = useState<'login' | 'register' | 'reset-password'>('login');

  useEffect(() => {
    // Check if we're on a password reset page
    const urlParams = new URLSearchParams(window.location.search);
    const resetToken = urlParams.get('token');
    
    if (resetToken && window.location.pathname === '/reset-password') {
      setAuthMode('reset-password');
      setIsLoading(false);
      return;
    }
    
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    const token = localStorage.getItem('access_token');
    
    if (!token) {
      setIsLoading(false);
      return;
    }

    try {
      // Set authorization header for API service FIRST
      apiService.setAuthToken(token);
      console.log('🔑 Setting auth token:', token.substring(0, 20) + '...');
      
      // Verify token and get user info
      const userResponse = await apiService.getCurrentUser();
      setUser(userResponse);
      console.log('✅ User authenticated:', userResponse.email);
      
    } catch (error) {
      console.error('❌ Auth check failed:', error);
      // Clear invalid tokens
      localStorage.removeItem('access_token');
      localStorage.removeItem('refresh_token');
      apiService.clearAuthToken();
    } finally {
      setIsLoading(false);
    }
  };

  const handleLoginSuccess = (userData: User, tokens: any) => {
    setUser(userData);
    apiService.setAuthToken(tokens.access_token);
  };

  const handleRegisterSuccess = (userData: User) => {
    // After registration, switch to login
    setAuthMode('login');
    notificationService.info('Please sign in with your new account');
  };

  const handleLogout = () => {
    localStorage.removeItem('access_token');
    localStorage.removeItem('refresh_token');
    apiService.clearAuthToken();
    setUser(null);
    notificationService.info('You have been logged out');
  };

  // Loading state
  if (isLoading) {
    return (
      <div className="auth-loading">
        <div className="text-center py-5">
          <div className="spinner-border text-primary mb-3" style={{ width: '3rem', height: '3rem' }}>
            <span className="visually-hidden">Loading...</span>
          </div>
          <h4 className="text-primary">AnCore</h4>
          <p className="text-muted">Loading your workspace...</p>
        </div>
      </div>
    );
  }

  // Not authenticated - show auth forms
  if (!user) {
    return (
      <>
        {authMode === 'login' ? (
          <Login
            onLoginSuccess={handleLoginSuccess}
            onSwitchToRegister={() => setAuthMode('register')}
          />
        ) : authMode === 'register' ? (
          <Register
            onRegisterSuccess={handleRegisterSuccess}
            onSwitchToLogin={() => setAuthMode('login')}
          />
        ) : authMode === 'reset-password' ? (
          <ResetPassword />
        ) : null}
      </>
    );
  }

  // Authenticated - render main app with user context
  return (
    <UserContext.Provider value={{ user, logout: handleLogout }}>
      {children}
    </UserContext.Provider>
  );
};

// User context for accessing user data throughout the app
export const UserContext = React.createContext<{
  user: User | null;
  logout: () => void;
}>({
  user: null,
  logout: () => {}
});

export default AuthWrapper;
